import { googleApi } from "@/api/googleApi";
import isSpacedString from "@/helpers/isSpacedString";
import { SearchPlace } from "@/interfaces/SearchPlace";
import { useSearchPlaceStore } from "@/stores/searchPlace";
import { useQuery } from "@tanstack/vue-query";
import { useDebounceFn } from "@vueuse/core";
import { set } from "lodash";
import { storeToRefs } from "pinia";
import { MaybeRef, isRef, watch } from "vue";

interface SearchBodyCategory {
  input: string;
  includedRegionCodes?: MaybeRef<string[]>;
  includedPrimaryTypes?: string[];
  languageCode?: string;
}

// Tipos reales que Places API (New) devuelve para resultados de
// ciudad/región/localidad. Antes se filtraba por "geocode", que no es un
// tipo de lugar válido en esta API (solo existía en la Autocomplete API
// vieja) — el bucket "Region o ciudad" quedaba siempre vacío sin importar
// la búsqueda.
const GEOGRAPHIC_PLACE_TYPES = new Set([
  "locality",
  "sublocality",
  "sublocality_level_1",
  "administrative_area_level_1",
  "administrative_area_level_2",
  "administrative_area_level_3",
  "political",
  "country",
  "postal_town",
]);

export interface SessionDataGoogle {
  sessionToken: string;
  placesUsedTimes: number;
  setNewSessionToken: () => void;
  addPlacesUsedTime: () => void;
  expireDate: Date | undefined;
  restApiValuesSearch: (renewSessionToken: boolean) => void;
  resetToken: () => void;
  resetUsedTimes: () => void;
}

interface Props {
  params?: Omit<SearchBodyCategory, "input">;
}

const getSearchPlaces = async (
  bodyParams: SearchBodyCategory,
  sessionData: SessionDataGoogle,
): Promise<SearchPlace | void> => {
  try {
    const { languageCode = "es" } = bodyParams;
    const {
      sessionToken,
      placesUsedTimes,
      expireDate,
      restApiValuesSearch,
      addPlacesUsedTime,
    } = sessionData;

    if (
      !sessionToken ||
      placesUsedTimes === 50 ||
      (expireDate && expireDate < new Date())
    )
      restApiValuesSearch(true);

    const regionCodes = isRef(bodyParams.includedRegionCodes)
      ? bodyParams.includedRegionCodes.value
      : bodyParams.includedRegionCodes;

    const { data } = await googleApi.post<SearchPlace>("/places:autocomplete", {
      ...bodyParams,
      languageCode,
      sessionToken: sessionToken,
      input: bodyParams.input,
      // Solo se restringe por país cuando la política del viajero trae
      // países explícitos. Sin ellos el campo viaja como undefined (axios lo
      // omite) para que la búsqueda sea internacional: forzar ["mx"] aquí
      // impedía encontrar destinos fuera de México.
      includedRegionCodes: regionCodes?.length ? regionCodes : undefined,
    });

    addPlacesUsedTime();

    return data;
  } catch (error) {
    // Error occurred
  }
};

export const useSearchPlaces = (props: Props = {}) => {
  const {
    setSearchPlaceQuery,
    setSearchPlaceResults,
    setNewSessionToken,
    addPlacesUsedTime,
    resetApiValuesSearch,
    resetUsedTimes,
    resetSessionToken,
    setListCountryCodes,
    setSuggestionPlaces,
    setPlaceDetails,
    setSelectedPlace,
    setFilteredResultsAirport,
    setFilteredResultsEstablishment,
    resetValues,
  } = useSearchPlaceStore();
  const {
    searchPlaceQuery,
    searchPlaceResults,
    sessionToken,
    placesUsedTimes,
    expiresDate,
    suggestionPlaces,
    placeSelected,
    placeDetails,
    suggestedFiltered,
    suggestedFilteredEstablishment,
  } = storeToRefs(useSearchPlaceStore());

  const { data, error, isFetching, refetch } = useQuery(
    ["searchPlaces"],
    () =>
      getSearchPlaces(
        {
          input: searchPlaceQuery.value,
          includedRegionCodes: props.params?.includedRegionCodes,
          includedPrimaryTypes: props.params?.includedPrimaryTypes,
        },
        {
          sessionToken: sessionToken.value,
          placesUsedTimes: placesUsedTimes.value,
          expireDate: expiresDate.value,
          setNewSessionToken,
          addPlacesUsedTime,
          restApiValuesSearch: resetApiValuesSearch,
          resetUsedTimes,
          resetToken: resetSessionToken,
        },
      ),
    {
      retry: 3,
      enabled: false,
    },
  );

  const searchPlaces = useDebounceFn(async () => {
    if (searchPlaceQuery.value && !isSpacedString(searchPlaceQuery.value)) {
      refetch();
    }
  }, 100);

  watch(data, (value): void => {
    if (value) {
      setSearchPlaceResults(value);
      // "geocode" nunca aparece en placePrediction.types en Places API (New) —
      // es una palabra reservada de la Autocomplete API vieja, no un tipo de
      // lugar real. Con este filtro el bucket "Region o ciudad" quedaba
      // siempre vacío, sin importar qué se buscara.
      setSuggestionPlaces(
        value.suggestions.filter((suggest) =>
          suggest.placePrediction.types?.some((type) =>
            GEOGRAPHIC_PLACE_TYPES.has(type),
          ),
        ),
      );

      setFilteredResultsAirport();
      setFilteredResultsEstablishment();
    }
  });

  watch(searchPlaceQuery, (value): void => {
    value ? searchPlaces() : setSearchPlaceResults(undefined);
    resetValues();
  });

  return {
    searchPlaceQuery,
    searchPlaceResults,
    isFetching,
    error,
    setSearchPlaceQuery,
    setNewSessionToken,
    setListCountryCodes,
    suggestionPlaces,
    setPlaceDetails,
    setSelectedPlace,
    placeSelected,
    suggestedFiltered,
    suggestedFilteredEstablishment,
  };
};
